<template>
  <div class="nx-list-page front-lift-execution-page">
    <avue-crud
      ref="crud"
      v-model="form"
      :option="option"
      :table-loading="loading"
      :data="data"
      :page.sync="page"
      :search.sync="query"
      :defaults.sync="defaults"
      :permission="permissionList"
      @search-change="searchChange"
      @search-reset="searchReset"
      @refresh-change="refreshChange"
      @current-change="currentChange"
      @size-change="sizeChange"
      @on-load="onLoad"
    >
      <template slot="menuLeft">
        <el-button
          v-if="permissionList.addBtn"
          type="primary"
          size="small"
          icon="el-icon-plus"
          @click="openAdd"
        >
          新增
        </el-button>
        <el-button
          v-if="permissionList.exportBtn"
          type="primary"
          size="small"
          plain
          icon="el-icon-download"
          :loading="exportLoading"
          @click="handleExport"
        >
          导出
        </el-button>
      </template>

      <template slot="contractNo" slot-scope="{ row }">
        <div>{{ row.contractNo || '-' }}</div>
        <div v-if="row.contractDetailId" class="sub-line">明细 {{ row.contractDetailId }}</div>
      </template>

      <template slot="custName" slot-scope="{ row }">
        <div>{{ row.custName || '-' }}</div>
        <div v-if="row.custNo" class="sub-line">{{ row.custNo }}</div>
      </template>

      <template slot="executeQuantity" slot-scope="{ row }">
        <span class="number-text">{{ formatQuantity(row.executeQuantity) }}</span>
      </template>

      <template slot="measureUnit" slot-scope="{ row }">
        <span>{{ row.measureUnitName || measureUnitLabel(row.measureUnit) }}</span>
      </template>

      <template slot="lockedFlag" slot-scope="{ row }">
        <matrix-tag :color="settlementStatusColor(row)" :text="settlementStatusLabel(row)" />
      </template>

      <template slot="menu" slot-scope="{ row }">
        <el-button
          v-if="permissionList.editBtn"
          type="text"
          size="small"
          :disabled="!canEdit(row)"
          @click="openEdit(row)"
        >
          编辑
        </el-button>
        <el-button
          v-if="permissionList.voidBtn"
          type="text"
          size="small"
          class="danger-text"
          :disabled="!canVoid(row)"
          @click="openVoid(row)"
        >
          作废
        </el-button>
        <el-button
          v-if="permissionList.logBtn"
          type="text"
          size="small"
          @click="openLogs(row)"
        >
          记录
        </el-button>
      </template>
    </avue-crud>

    <el-dialog
      :title="dialogTitle"
      :visible.sync="editVisible"
      width="680px"
      append-to-body
      :close-on-click-modal="false"
      @closed="resetEditForm"
    >
      <el-form ref="editForm" :model="editForm" :rules="editRules" label-width="110px">
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="补录日期" prop="executeDate">
              <el-date-picker
                v-model="editForm.executeDate"
                type="date"
                value-format="yyyy-MM-dd"
                placeholder="请选择补录日期"
                style="width: 100%"
                @change="handleExecuteDateChange"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="执行量" prop="executeQuantity">
              <el-input-number
                v-model="editForm.executeQuantity"
                :min="1"
                :precision="0"
                :step="1"
                controls-position="right"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="合同" prop="contractKey">
              <el-select
                v-model="editForm.contractKey"
                class="form-block"
                filterable
                remote
                reserve-keyword
                clearable
                placeholder="请选择合同编号或名称"
                :remote-method="remoteContractSearch"
                :loading="contractLoading"
                :disabled="!editForm.executeDate"
                @focus="ensureContractOptions"
                @change="handleContractChange"
              >
                <el-option
                  v-for="item in contractOptions"
                  :key="contractOptionValue(item)"
                  :label="contractOptionLabel(item)"
                  :value="contractOptionValue(item)"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="客户">
              <el-input :value="customerText" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="计量单位">
              <el-input value="箱数" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="不含税单价">
              <el-input :value="editForm.unitPriceWithoutTax || editForm.unitPrice || '-'" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="税率">
              <el-input :value="taxRateText" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="备注" prop="remark">
              <el-input v-model="editForm.remark" type="textarea" :rows="3" maxlength="500" show-word-limit />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="editVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="submitLoading" @click="handleSubmit">确定</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="作废正面吊执行量补录"
      :visible.sync="voidVisible"
      width="520px"
      append-to-body
      :close-on-click-modal="false"
      @closed="resetVoidForm"
    >
      <el-form ref="voidForm" :model="voidForm" :rules="voidRules" label-width="100px">
        <el-form-item label="合同编号">
          <el-input :value="voidRow && voidRow.contractNo" disabled />
        </el-form-item>
        <el-form-item label="补录日期">
          <el-input :value="voidRow && voidRow.executeDate" disabled />
        </el-form-item>
        <el-form-item label="作废原因" prop="voidReason">
          <el-input v-model="voidForm.voidReason" type="textarea" :rows="3" maxlength="200" show-word-limit />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="voidVisible = false">取消</el-button>
        <el-button type="danger" size="small" :loading="voidLoading" @click="handleVoidSubmit">确定作废</el-button>
      </span>
    </el-dialog>

    <el-drawer
      title="操作记录"
      :visible.sync="logVisible"
      direction="rtl"
      size="560px"
      append-to-body
      @closed="resetLogs"
    >
      <div v-if="logRow" class="log-summary">
        <dl>
          <dt>合同编号</dt>
          <dd>{{ logRow.contractNo || '-' }}</dd>
          <dt>补录日期</dt>
          <dd>{{ logRow.executeDate || '-' }}</dd>
          <dt>执行量</dt>
          <dd>{{ formatQuantity(logRow.executeQuantity) }} 箱</dd>
          <dt>状态</dt>
          <dd>{{ settlementStatusLabel(logRow) }}</dd>
        </dl>
      </div>
      <div v-loading="logLoading" class="log-body">
        <el-empty v-if="!logLoading && logRows.length === 0" description="暂无操作记录" />
        <el-timeline v-else>
          <el-timeline-item
            v-for="item in logRows"
            :key="item.id || item.operationTime"
            :timestamp="item.operationTime || item.createTime"
            placement="top"
          >
            <div class="log-title">{{ operationTypeLabel(item.operationType) }}</div>
            <div class="sub-line">{{ item.operatorName || item.createName || '-' }}</div>
            <div class="log-remark">{{ item.operationRemark || item.remark || '-' }}</div>
            <el-collapse v-if="item.beforeContent || item.afterContent" class="log-content">
              <el-collapse-item title="变更内容" :name="String(item.id || item.operationTime)">
                <div v-if="item.beforeContent" class="content-block">
                  <div class="content-title">操作前</div>
                  <pre>{{ formatContent(item.beforeContent) }}</pre>
                </div>
                <div v-if="item.afterContent" class="content-block">
                  <div class="content-title">操作后</div>
                  <pre>{{ formatContent(item.afterContent) }}</pre>
                </div>
              </el-collapse-item>
            </el-collapse>
          </el-timeline-item>
        </el-timeline>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import crudMixins from '@/mixins/crud-mixins';
import matrixTag from '@/components/matrix/matrix-tag';
import { downloadXls } from '@/util/util';
import {
  add,
  exportExcel,
  getDetail,
  getList,
  getLogs,
  selectContracts,
  update,
  voidRecord,
} from './api';
import {
  FRONT_LIFT_PERMISSION,
  LOCKED_FLAG_OPTIONS,
  MEASURE_UNIT_BOX,
  RECORD_STATUS_OPTIONS,
  isLockedRecord,
  isVoidedStatus,
  measureUnitLabel,
  operationTypeLabel,
  settlementStatusLabel,
} from './const';

function createEditForm() {
  return {
    id: '',
    executeDate: '',
    contractKey: '',
    contractId: '',
    contractDetailId: '',
    contractNo: '',
    contractName: '',
    custNo: '',
    custName: '',
    executeQuantity: undefined,
    measureUnit: MEASURE_UNIT_BOX,
    unitPrice: '',
    unitPriceWithoutTax: '',
    taxRate: '',
    settleMethod: '',
    remark: '',
  };
}

function createVoidForm() {
  return {
    id: '',
    voidReason: '',
  };
}

export default {
  name: 'finance-front-lift-execution',
  components: { matrixTag },
  mixins: [crudMixins],
  data() {
    const validateQuantity = (rule, value, callback) => {
      if (value === undefined || value === null || value === '') {
        callback(new Error('请输入执行量'));
        return;
      }
      if (!Number.isInteger(Number(value)) || Number(value) <= 0) {
        callback(new Error('执行量必须为大于 0 的整数'));
        return;
      }
      callback();
    };

    return {
      apiConfig: { getList, getDetail },
      exportLoading: false,
      submitLoading: false,
      contractLoading: false,
      voidLoading: false,
      logLoading: false,
      editVisible: false,
      voidVisible: false,
      logVisible: false,
      editMode: 'add',
      contractOptions: [],
      editForm: createEditForm(),
      voidForm: createVoidForm(),
      voidRow: null,
      logRow: null,
      logRows: [],
      editRules: {
        executeDate: [{ required: true, message: '请选择补录日期', trigger: 'change' }],
        contractKey: [{ required: true, message: '请选择合同', trigger: 'change' }],
        executeQuantity: [{ required: true, validator: validateQuantity, trigger: 'change' }],
        remark: [{ max: 500, message: '备注最多 500 个字符', trigger: 'blur' }],
      },
      voidRules: {
        voidReason: [
          { required: true, message: '请输入作废原因', trigger: 'blur' },
          { max: 200, message: '作废原因最多 200 个字符', trigger: 'blur' },
        ],
      },
      option: {
        ...this.$store.state.publicOption,
        addBtn: false,
        editBtn: false,
        delBtn: false,
        viewBtn: false,
        menuWidth: 180,
        labelWidth: 110,
        searchMenuSpanSuper: 6,
        column: [
          {
            label: '合同编号',
            prop: 'contractNo',
            slot: true,
            search: true,
            searchSpan: 6,
            minWidth: 160,
            overHidden: true,
          },
          {
            label: '合同名称',
            prop: 'contractName',
            search: true,
            searchSpan: 6,
            minWidth: 180,
            overHidden: true,
          },
          {
            label: '客户',
            prop: 'custName',
            slot: true,
            search: true,
            searchSpan: 6,
            minWidth: 150,
            overHidden: true,
          },
          {
            label: '补录日期',
            prop: 'executeDate',
            type: 'date',
            format: 'yyyy-MM-dd',
            valueFormat: 'yyyy-MM-dd',
            width: 120,
          },
          {
            label: '补录日期',
            prop: 'executeDateRange',
            type: 'daterange',
            search: true,
            searchRange: true,
            searchSpan: 6,
            hide: true,
            display: false,
            showColumn: false,
            format: 'yyyy-MM-dd',
            valueFormat: 'yyyy-MM-dd',
            clearable: true,
            paramsKey: ['executeStartDate', 'executeEndDate'],
          },
          {
            label: '执行量',
            prop: 'executeQuantity',
            slot: true,
            width: 100,
            align: 'right',
          },
          {
            label: '单位',
            prop: 'measureUnit',
            slot: true,
            width: 80,
          },
          {
            label: '结算状态',
            prop: 'lockedFlag',
            type: 'select',
            slot: true,
            search: true,
            searchSpan: 6,
            width: 120,
            dicData: LOCKED_FLAG_OPTIONS,
          },
          {
            label: '记录状态',
            prop: 'status',
            type: 'select',
            width: 90,
            dicData: RECORD_STATUS_OPTIONS,
            hide: true,
          },
          {
            label: '录入人',
            prop: 'createName',
            width: 110,
            overHidden: true,
          },
          {
            label: '录入时间',
            prop: 'createTime',
            type: 'datetime',
            format: 'yyyy-MM-dd HH:mm:ss',
            valueFormat: 'yyyy-MM-dd HH:mm:ss',
            width: 170,
          },
          {
            label: '备注',
            prop: 'remark',
            minWidth: 180,
            overHidden: true,
            hide: true,
          },
        ],
      },
    };
  },
  computed: {
    ...mapGetters(['permission']),
    permissionList() {
      return {
        addBtn: this.hasPermission(FRONT_LIFT_PERMISSION.add),
        editBtn: this.hasPermission(FRONT_LIFT_PERMISSION.edit),
        delBtn: false,
        viewBtn: this.hasPermission(FRONT_LIFT_PERMISSION.view),
        voidBtn: this.hasPermission(FRONT_LIFT_PERMISSION.void),
        exportBtn: this.hasPermission(FRONT_LIFT_PERMISSION.export),
        logBtn: this.hasPermission(FRONT_LIFT_PERMISSION.log),
      };
    },
    dialogTitle() {
      return this.editMode === 'edit' ? '编辑正面吊执行量补录' : '新增正面吊执行量补录';
    },
    customerText() {
      return [this.editForm.custNo, this.editForm.custName].filter(Boolean).join(' / ') || '-';
    },
    taxRateText() {
      if (this.editForm.taxRate === '' || this.editForm.taxRate === null || this.editForm.taxRate === undefined) {
        return '-';
      }
      return `${this.editForm.taxRate}%`;
    },
  },
  methods: {
    hasPermission(code) {
      return this.vaildData(this.permission && this.permission[code], false);
    },
    measureUnitLabel,
    operationTypeLabel,
    settlementStatusLabel,
    settlementStatusColor(row) {
      if (isVoidedStatus(row.status)) {
        return '#64748b';
      }
      return isLockedRecord(row) ? '#909399' : '#166534';
    },
    formatQuantity(value) {
      if (value === undefined || value === null || value === '') {
        return '-';
      }
      const numberValue = Number(String(value).replace(/,/g, ''));
      if (Number.isNaN(numberValue)) {
        return value;
      }
      return numberValue.toLocaleString('zh-CN', { maximumFractionDigits: 0 });
    },
    canEdit(row) {
      return !isLockedRecord(row) && !isVoidedStatus(row.status);
    },
    canVoid(row) {
      return !isLockedRecord(row) && !isVoidedStatus(row.status);
    },
    contractOptionValue(item) {
      return `${item.contractId || item.id || ''}_${item.contractDetailId || ''}`;
    },
    contractOptionLabel(item) {
      return [
        item.contractNo,
        item.contractName,
        item.custName ? `客户:${item.custName}` : '',
      ].filter(Boolean).join(' / ');
    },
    normalizeContractOptions(data) {
      if (Array.isArray(data)) {
        return data;
      }
      if (data && Array.isArray(data.records)) {
        return data.records;
      }
      return [];
    },
    normalizeDetail(row) {
      return {
        ...createEditForm(),
        ...row,
        contractKey: row.contractId || row.id
          ? `${row.contractId || row.id}_${row.contractDetailId || ''}`
          : '',
        measureUnit: row.measureUnit || MEASURE_UNIT_BOX,
      };
    },
    openAdd() {
      this.editMode = 'add';
      this.editForm = createEditForm();
      this.contractOptions = [];
      this.editVisible = true;
    },
    async openEdit(row) {
      if (!this.canEdit(row)) {
        this.$message.warning('该记录已锁定或已作废，不能编辑');
        return;
      }
      this.editMode = 'edit';
      this.submitLoading = true;
      try {
        const res = await getDetail(row.id);
        const detail = res.data.data || res.data || row;
        this.editForm = this.normalizeDetail(detail);
        this.contractOptions = [this.editForm];
        this.editVisible = true;
      } catch (e) {
        console.warn(e);
        this.$message.error('获取详情失败');
      } finally {
        this.submitLoading = false;
      }
    },
    resetEditForm() {
      this.editForm = createEditForm();
      this.contractOptions = [];
      this.$refs.editForm && this.$refs.editForm.clearValidate();
    },
    handleExecuteDateChange() {
      this.editForm.contractKey = '';
      this.editForm.contractId = '';
      this.editForm.contractDetailId = '';
      this.editForm.contractNo = '';
      this.editForm.contractName = '';
      this.editForm.custNo = '';
      this.editForm.custName = '';
      this.editForm.unitPrice = '';
      this.editForm.unitPriceWithoutTax = '';
      this.editForm.taxRate = '';
      this.editForm.settleMethod = '';
      this.contractOptions = [];
      if (this.editForm.executeDate) {
        this.remoteContractSearch('');
      }
    },
    ensureContractOptions() {
      if (!this.editForm.executeDate) {
        this.$message.warning('请先选择补录日期');
        return;
      }
      if (this.contractOptions.length === 0) {
        this.remoteContractSearch('');
      }
    },
    async remoteContractSearch(keyword) {
      if (!this.editForm.executeDate) {
        this.contractOptions = [];
        return;
      }
      this.contractLoading = true;
      try {
        const res = await selectContracts({
          executeDate: this.editForm.executeDate,
          keyword,
        });
        this.contractOptions = this.normalizeContractOptions(res.data.data || res.data);
      } catch (e) {
        console.warn(e);
        this.contractOptions = [];
        this.$message.error('合同检索失败');
      } finally {
        this.contractLoading = false;
      }
    },
    handleContractChange(value) {
      const hit = this.contractOptions.find((item) => this.contractOptionValue(item) === value);
      if (!hit) {
        this.editForm.contractId = '';
        this.editForm.contractDetailId = '';
        this.editForm.contractNo = '';
        this.editForm.contractName = '';
        this.editForm.custNo = '';
        this.editForm.custName = '';
        return;
      }
      this.editForm.contractId = hit.contractId || hit.id || '';
      this.editForm.contractDetailId = hit.contractDetailId || '';
      this.editForm.contractNo = hit.contractNo || '';
      this.editForm.contractName = hit.contractName || '';
      this.editForm.custNo = hit.custNo || '';
      this.editForm.custName = hit.custName || '';
      this.editForm.unitPrice = hit.unitPrice || '';
      this.editForm.unitPriceWithoutTax = hit.unitPriceWithoutTax || hit.unitPrice || '';
      this.editForm.taxRate = hit.taxRate || '';
      this.editForm.settleMethod = hit.settleMethod || '';
    },
    buildSubmitPayload() {
      return {
        id: this.editForm.id,
        contractId: this.editForm.contractId,
        contractDetailId: this.editForm.contractDetailId,
        executeDate: this.editForm.executeDate,
        executeQuantity: this.editForm.executeQuantity,
        remark: this.editForm.remark,
      };
    },
    handleSubmit() {
      this.$refs.editForm.validate(async (valid) => {
        if (!valid) return;
        this.submitLoading = true;
        try {
          const payload = this.buildSubmitPayload();
          if (this.editMode === 'edit') {
            await update(payload);
            this.$message.success('保存成功');
          } else {
            await add(payload);
            this.$message.success('新增成功');
          }
          this.editVisible = false;
          this.refreshChange();
        } catch (e) {
          console.warn(e);
          this.$message.error((e && e.message) || '保存失败');
        } finally {
          this.submitLoading = false;
        }
      });
    },
    openVoid(row) {
      if (!this.canVoid(row)) {
        this.$message.warning('该记录已锁定或已作废，不能作废');
        return;
      }
      this.voidRow = row;
      this.voidForm = {
        id: row.id,
        voidReason: '',
      };
      this.voidVisible = true;
    },
    resetVoidForm() {
      this.voidRow = null;
      this.voidForm = createVoidForm();
      this.$refs.voidForm && this.$refs.voidForm.clearValidate();
    },
    handleVoidSubmit() {
      this.$refs.voidForm.validate(async (valid) => {
        if (!valid) return;
        this.voidLoading = true;
        try {
          await voidRecord({ ...this.voidForm });
          this.$message.success('作废成功');
          this.voidVisible = false;
          this.refreshChange();
        } catch (e) {
          console.warn(e);
          this.$message.error((e && e.message) || '作废失败');
        } finally {
          this.voidLoading = false;
        }
      });
    },
    async openLogs(row) {
      this.logRow = row;
      this.logVisible = true;
      this.logLoading = true;
      try {
        const res = await getLogs(row.id);
        const data = res.data.data || res.data || [];
        this.logRows = Array.isArray(data) ? data : (data.records || []);
      } catch (e) {
        console.warn(e);
        this.logRows = [];
        this.$message.error('操作记录加载失败');
      } finally {
        this.logLoading = false;
      }
    },
    resetLogs() {
      this.logRow = null;
      this.logRows = [];
    },
    formatContent(content) {
      if (!content) {
        return '-';
      }
      if (typeof content !== 'string') {
        return JSON.stringify(content, null, 2);
      }
      try {
        return JSON.stringify(JSON.parse(content), null, 2);
      } catch (e) {
        return content;
      }
    },
    async handleExport() {
      const total = this.page.total || 0;
      if (total < 1) {
        this.$message.warning('无数据可导出');
        return;
      }
      if (total > 10000) {
        this.$message.warning(`当前最多导出 10000 条，请缩小筛选范围后重试`);
        return;
      }
      this.exportLoading = true;
      try {
        const params = this.formatParams(Object.assign({}, this.query));
        const res = await exportExcel(params);
        downloadXls(res.data, `正面吊执行量补录_${Date.now()}.xlsx`);
        this.$message.success('导出成功');
      } catch (e) {
        console.warn(e);
        this.$message.error((e && e.message) || '导出失败');
      } finally {
        this.exportLoading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.front-lift-execution-page {
  .sub-line {
    font-size: 12px;
    color: #86909c;
    line-height: 1.4;
  }

  .number-text {
    font-variant-numeric: tabular-nums;
  }

  .danger-text {
    color: #f56c6c;

    &.is-disabled {
      color: #c0c4cc;
    }
  }
}

.form-block {
  width: 100%;
}

.log-summary {
  padding: 0 20px 12px;

  dl {
    display: grid;
    grid-template-columns: 92px 1fr;
    gap: 8px 12px;
    margin: 0;
    padding: 12px;
    border: 1px solid #ebeef5;
    background: #fafafa;
  }

  dt {
    color: #86909c;
  }

  dd {
    margin: 0;
    color: #303133;
    word-break: break-all;
  }
}

.log-body {
  min-height: 220px;
  padding: 4px 20px 20px;
}

.log-title {
  font-weight: 600;
  color: #303133;
}

.log-remark {
  margin-top: 4px;
  color: #606266;
}

.log-content {
  margin-top: 8px;
}

.content-block {
  & + .content-block {
    margin-top: 10px;
  }

  .content-title {
    margin-bottom: 4px;
    color: #86909c;
  }

  pre {
    max-height: 180px;
    margin: 0;
    padding: 8px;
    overflow: auto;
    white-space: pre-wrap;
    word-break: break-all;
    background: #f5f7fa;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    font-size: 12px;
    line-height: 1.5;
  }
}
</style>
